import GUEST_STREAK_LADDER from '../consts/guestStreakLadder';
import { HIDDEN_STATE } from './streakHelpers';
import { PromoItemStatuses, StreakSheetModes } from '../types';
import type {
  Promo,
  PromoItemsResponse,
  StreakDay,
  StreakViewState,
} from '../types';

const GUEST_DAY_NUMBER = 1;

interface GuestLadderEntry {
  creditAmount: number | null;
  rewardName: string;
}

/**
 * The anonymous items response, in day order. Amounts and names are taken
 * verbatim so a guest reads exactly what a signed-in day-1 user reads —
 * including `itemName`, which is CMS free text the client must not re-derive.
 */
const ladderFromItems = (
  itemsResponse: PromoItemsResponse | null | undefined
): GuestLadderEntry[] | null => {
  const items = itemsResponse?.items;
  if (!items?.length) return null;

  return [...items]
    .sort((first, second) => first.item.periodIndex - second.item.periodIndex)
    .map(({ item }) => ({
      creditAmount: item.creditAmount,
      rewardName: item.itemName,
    }));
};

const ladderFromConfig = (): GuestLadderEntry[] | null =>
  GUEST_STREAK_LADDER.length
    ? GUEST_STREAK_LADDER.map((creditAmount) => ({
        creditAmount,
        rewardName: `${creditAmount} Credits`,
      }))
    : null;

const BLANK_ENTRY: GuestLadderEntry = { creditAmount: null, rewardName: '' };

/**
 * Server ladder first, `VITE_FREE_PASS_GUEST_LADDER` second, nothing third.
 *
 * The length check is what keeps a stale fallback from misaligning: a config
 * array written for a 7-day campaign must not be mapped onto a 5-day one. An
 * unusable ladder renders a shape-only track, never invented numbers.
 */
const resolveLadder = ({
  itemsResponse,
  totalDays,
}: {
  itemsResponse: PromoItemsResponse | null | undefined;
  totalDays: number;
}): GuestLadderEntry[] => {
  const candidates = [ladderFromItems(itemsResponse), ladderFromConfig()];
  const usable = candidates.find((ladder) => ladder?.length === totalDays);

  return usable ?? Array.from({ length: totalDays }, () => BLANK_ENTRY);
};

const toGuestDay = ({
  dayIndex,
  ladder,
}: {
  dayIndex: number;
  ladder: GuestLadderEntry[];
}): StreakDay => ({
  dayNumber: dayIndex + 1,
  // Day 1 claimable, the rest locked — computed here, never read from the
  // response, so no server answer can advance a guest.
  status: dayIndex === 0 ? PromoItemStatuses.Active : PromoItemStatuses.Locked,
  creditAmount: ladder[dayIndex].creditAmount,
  rewardName: ladder[dayIndex].rewardName,
});

/**
 * The read model for a logged-out visitor: day 1, always.
 *
 * The only thing taken from the network is the reward ladder. There is no
 * clock, no storage and no claim history here, so no input *could* move the day
 * forward — that is what makes "a guest never advances" a property of the code
 * rather than a rule to remember. `totalGifts` is real server data, so the
 * track is always the right length.
 *
 * Shape-compatible with `deriveStreakState` so the pill and sheet render one
 * `StreakViewState` and never learn which branch produced it.
 */
export const buildGuestStreakState = ({
  promo,
  itemsResponse,
}: {
  promo: Promo | null | undefined;
  itemsResponse?: PromoItemsResponse | null;
}): StreakViewState => {
  if (!promo || promo.totalGifts < GUEST_DAY_NUMBER) return HIDDEN_STATE;

  const totalDays = promo.totalGifts;
  const ladder = resolveLadder({ itemsResponse, totalDays });
  const days = Array.from({ length: totalDays }, (_unused, dayIndex) =>
    toGuestDay({ dayIndex, ladder })
  );

  return {
    isVisible: true,
    days,
    currentDayNumber: GUEST_DAY_NUMBER,
    // A one-day campaign is its own prize day; a 7-day one is not, so a guest
    // gets the same pink day-1 theme a real day-1 user gets.
    isFinalDay: totalDays === GUEST_DAY_NUMBER,
    canClaim: true,
    isWaiting: false,
    heroDay: days[0],
    // Eligible, not yet joined. Also lights day 1 via `isNotJoined`.
    sheetMode: StreakSheetModes.NotJoined,
    nextClaimAt: null,
    prizeName: days[totalDays - 1].rewardName || null,
  };
};
